import { useState, useEffect, useCallback } from "react";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  getDocsFromServer,
  QuerySnapshot,
  type DocumentData,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { type TranscriptionData } from "@/redux/api/transcriptions.rtkq";

interface UseRealtimeTranscriptionsProps {
  userId: string;
  enabled?: boolean;
}

interface UseRealtimeTranscriptionsReturn {
  transcriptions: TranscriptionData[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

// Helper function to convert Firestore document to TranscriptionData
const docToTranscription = (doc: DocumentData): TranscriptionData => {
  const data = doc.data();
  if (!data) {
    throw new Error("Document data is undefined");
  }
  return {
    id: doc.id,
    userId: data.userId,
    fileName: data.fileName,
    storagePath: data.storagePath,
    transcription: data.transcription || "",
    caseId: data.caseId,
    editorState: data.editorState,
    metadata: {
      duration: data.metadata?.duration || 0,
      fileSize: data.metadata?.fileSize || 0,
      chunksProcessed: data.metadata?.chunksProcessed || 0,
      language: data.metadata?.language || "he",
      model: data.metadata?.model || "whisper-large-v3",
    },
    processing: {
      status: data.processing?.status || "processing",
      startedAt: data.processing?.startedAt || new Date().toISOString(),
      completedAt: data.processing?.completedAt,
      processingTimeMs: data.processing?.processingTimeMs,
      error: data.processing?.error,
      errorCode: data.processing?.errorCode,
    },
    summary: data.summary,
    questionsFromAi: data.questionsFromAi || [],
    isDeleted: data.isDeleted || false,
    deletedAt: data.deletedAt,
    createdAt: data.createdAt || new Date().toISOString(),
    updatedAt: data.updatedAt || new Date().toISOString(),
  };
};

export const useRealtimeTranscriptions = ({
  userId,
  enabled = true,
}: UseRealtimeTranscriptionsProps): UseRealtimeTranscriptionsReturn => {
  const [transcriptions, setTranscriptions] = useState<TranscriptionData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [refresh, setRefresh] = useState(0);

  const refetch = useCallback(() => {
    setRefresh(value => value + 1);
    setIsLoading(true);
    setError(null);
  }, []);

  useEffect(() => {
    setTranscriptions([]);
    if (!userId || !enabled) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const transcriptionsRef = collection(db, "users", userId, "transcriptions");
      const q = query(transcriptionsRef, orderBy("createdAt", "desc"));

      let active = true;
      let hasPending = false;
      let reading = false;
      const readServer = async () => {
        if (reading) return;
        reading = true;
        try {
          const snapshot = await getDocsFromServer(q);
          if (active) {
            const rows = snapshot.docs.map(docToTranscription);
            hasPending = rows.some(row => ["queued", "processing"].includes(row.processing.status) || row.summary?.summaryStatus === "processing");
            setTranscriptions(rows);
            setError(null);
          }
        } catch (err) {
          if (active) setError(err instanceof Error ? err.message : "Status unavailable");
        } finally {
          reading = false;
          if (active) setIsLoading(false);
        }
      };
      // Elapsed timer does not depend on the user's wall clock. It never changes business status.
      const timer = setInterval(() => { if (hasPending) void readServer(); }, 60_000);
      void readServer();
      const unsubscribe = onSnapshot(
        q,
        (querySnapshot: QuerySnapshot<DocumentData>) => {
          try {
            const newTranscriptions = querySnapshot.docs.map(docToTranscription);
            // newTranscriptions.forEach((t) => console.log("🔄Real-time update received:", t.fileName, t.processing.status));
            hasPending = newTranscriptions.some(row => ["queued", "processing"].includes(row.processing.status) || row.summary?.summaryStatus === "processing");
            setTranscriptions(newTranscriptions);
            setError(null);
          } catch (err) {
            console.error("Error processing transcription documents:", err);
            setError(err instanceof Error ? err.message : "Unknown error");
          } finally {
            setIsLoading(false);
          }
        },
        (err) => {
          console.error("Error listening to transcriptions:", err);
          setError(err.message);
          setIsLoading(false);
        }
      );

      return () => {
        active = false;
        clearInterval(timer);
        unsubscribe();
      };
    } catch (err) {
      console.error("Error setting up transcriptions listener:", err);
      setError(err instanceof Error ? err.message : "Unknown error");
      setIsLoading(false);
    }
  }, [userId, enabled, refresh]);

  return {
    transcriptions,
    isLoading,
    error,
    refetch,
  };
};
